import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "./AdminLayout";
import { MENU_GUIDELINE_CONFIG, READINESS_META } from "@/config/menu-guideline-status";
import FlowMap from "@/components/menu-guideline/FlowMap";
import MetricPanel from "@/components/menu-guideline/MetricPanel";
import GuidePanel from "@/components/menu-guideline/GuidePanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Activity, ShoppingCart, ShoppingBag, Warehouse, Landmark, CalendarRange,
  Users, FolderLock, ChefHat, Building2, FileSpreadsheet, ScanLine, BookOpen,
} from "lucide-react";

// Icon resolver — keep imports static so CRA tree-shake stays happy.
const ICONS = {
  Activity, ShoppingCart, ShoppingBag, Warehouse, Landmark, CalendarRange,
  Users, FolderLock, ChefHat, Building2, FileSpreadsheet, ScanLine,
};

function ReadinessBadge({ readiness }) {
  const r = READINESS_META[readiness] || READINESS_META.ready;
  return <Badge className={`${r.pill} border-0`} data-testid={`readiness-${readiness}`}>{r.label}</Badge>;
}

function PostsToLedger({ entries }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-sm px-4 py-3"
         data-testid="posts-to-ledger">
      <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold mb-1.5">
        Posts to ledger
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
        {entries.map((e, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${
              e.dr ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"
            }`}>{e.dr ? "Dr" : "Cr"}</span>
            <span className="text-stone-700">{e.account}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MenuGuideline() {
  const [params, setParams] = useSearchParams();
  const tabKeyFromUrl = params.get("tab");
  const initialKey = useMemo(() => {
    const valid = MENU_GUIDELINE_CONFIG.find(t => t.key === tabKeyFromUrl);
    return valid?.key || MENU_GUIDELINE_CONFIG[0].key;
  }, [tabKeyFromUrl]);
  const [activeKey, setActiveKey] = useState(initialKey);
  const [guideOpenFor, setGuideOpenFor] = useState(null);

  useEffect(() => { setActiveKey(initialKey); }, [initialKey]);

  const activeTab = MENU_GUIDELINE_CONFIG.find(t => t.key === activeKey) || MENU_GUIDELINE_CONFIG[0];

  const onTabClick = (key) => {
    setActiveKey(key);
    setParams({ tab: key }, { replace: true });
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div data-testid="menu-guideline-page">
        <PageHeader
          kicker="Hướng dẫn Menu"
          title="Flow Maps · Toàn cảnh chức năng Thanh Sơn"
          breadcrumb={["Thanh Sơn ERP", "Tổng quan", "Menu Guideline"]}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGuideOpenFor(activeTab)}
              disabled={!activeTab.hasGuide}
              data-testid="open-guide-btn">
              <BookOpen className="w-4 h-4 mr-1.5" />
              {activeTab.hasGuide ? "Mở hướng dẫn" : "Hướng dẫn sắp ra mắt"}
            </Button>
          }
        />

        <div className="p-6 lg:p-8 space-y-6">
          {/* Tab bar */}
          <div className="flex flex-wrap gap-1.5" data-testid="menu-guideline-tabs">
            {MENU_GUIDELINE_CONFIG.map((t) => {
              const Icon = ICONS[t.icon] || Activity;
              const isActive = t.key === activeKey;
              const r = READINESS_META[t.readiness];
              return (
                <button
                  key={t.key}
                  onClick={() => onTabClick(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors
                    border ${isActive
                      ? "bg-emerald-900 text-white border-emerald-900 font-semibold"
                      : "bg-white text-stone-700 border-stone-200 hover:border-emerald-700 font-medium"}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                  <span>{t.label}</span>
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded
                    ${isActive ? "bg-white/15 text-white" : r.pill}`}>
                    {r.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active tab content */}
          <section className="bg-white border border-stone-200 rounded-sm p-5 lg:p-6 space-y-5"
                   data-testid={`tab-content-${activeTab.key}`}>
            {/* Header row: title + metrics */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="font-serif text-xl font-bold text-stone-900">{activeTab.label}</h2>
                  <ReadinessBadge readiness={activeTab.readiness} />
                  {!activeTab.hasGuide && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-[10px] border-stone-300 text-stone-500"
                               data-testid={`no-guide-${activeTab.key}`}>
                          Hướng dẫn sắp ra mắt
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>{activeTab.noGuideTooltip || "Hướng dẫn sẽ ra mắt trong issue follow-up."}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="text-sm text-stone-500">
                  {activeTab.layout === "hub"
                    ? "Sơ đồ trung tâm — nguồn dữ liệu chảy vào & báo cáo chảy ra."
                    : "Sơ đồ step-by-step. Click vào phase đã sẵn sàng để mở module."}
                </div>
              </div>
              <MetricPanel metrics={activeTab.metrics} />
            </div>

            {/* Flow diagram — clicks on Ready phases navigate via FlowMap's internal router. */}
            <FlowMap tab={activeTab} />

            {/* Posts-to-ledger */}
            <PostsToLedger entries={activeTab.postsToLedger} />
          </section>
        </div>

        <GuidePanel
          tab={guideOpenFor}
          open={Boolean(guideOpenFor)}
          onOpenChange={(v) => !v && setGuideOpenFor(null)}
        />
      </div>
    </TooltipProvider>
  );
}
